import { AddUserIcon, AttachIcon, CheckboxRoundActiveIcon, CommentNoteIcon } from '@synergy/theme-next';
import { BonusSettlementStatus, BonusSettlementSupplierStatus, Media } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { FormikProps, useFormikContext } from 'formik';
import { Paper, Stack, useTheme } from '@mui/material';
import { PoolAttachmentsModal, PoolButton as PoolNextButton, PoolTooltip } from '@synergy/components-next';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import match from 'autosuggest-highlight/match';

import { BonusSettlementSupplierDetailAddCustomerModal } from './components/bonus-settlement-supplier-detail-add-customer-modal';
import { BonusSettlementSupplierDetailCommentModal } from './components/bonus-settlement-supplier-detail-comment-modal';
import { BonusSettlementSupplierDetailFormValues } from './models/bonus-settlement-supplier-detail-form-values';
import { bonusSettlementsSupplierDetailSubmittingSelector } from './store/bonus-settlements-supplier-detail-selector';
import { updateSupplierBonusSettlementFiles } from './store/bonus-settlements-supplier-detail-actions';
import { useAppSelector } from '../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

type BonusSettlementSupplierDetailFooterProps = {
    readOnly: boolean;
    documents: Media[];
    bonusSettlementStatus: BonusSettlementStatus;
    bonusSettlementSupplierStatus: BonusSettlementSupplierStatus;
};

export const BonusSettlementSupplierDetailFooter = ({
    readOnly,
    documents,
    bonusSettlementStatus,
    bonusSettlementSupplierStatus,
}: BonusSettlementSupplierDetailFooterProps) => {
    const dispatch = useThunkDispatch();
    const theme = useTheme();
    const { bonusSettlementId, id: supplierBonusSettlementId } = useParams<{ bonusSettlementId: string; id: string }>();
    const privateFilesystemPath = useAppSelector(state => state.settings.privateFilesystemPath);
    const isSubmitting = useAppSelector(bonusSettlementsSupplierDetailSubmittingSelector);
    const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
    const [isFilesModalOpen, setIsFilesModalOpen] = useState(false);
    const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
    const formikProps = useFormikContext<BonusSettlementSupplierDetailFormValues>();
    const [searchParams] = useSearchParams();
    const [filesCount, setFilesCount] = useState<number>(0);

    useEffect(() => {
        setFilesCount(documents.length);
    }, [documents]);

    const handleAllFinishedClick = (formikProps: FormikProps<BonusSettlementSupplierDetailFormValues>) => {
        const search = searchParams.get('search');
        const isFinished = searchParams.get('isFinished') === 'true';
        const hasNoteToCustomer = searchParams.get('hasNoteToCustomer');

        if (isFinished) {
            return;
        }

        formikProps.values.customerBonusSettlements.forEach((settlement, index) => {
            if (settlement && settlement.isFinished) {
                return;
            }

            if (search) {
                const nameMatches = match(settlement.customerName, search, {
                    requireMatchAll: true,
                    insideWords: true,
                });
                const idMatches = match(settlement.customerId.toString(), search);
                if (nameMatches.length === 0 && idMatches.length === 0) {
                    return;
                }
            }

            if (hasNoteToCustomer === 'true' && (settlement.noteToCustomer?.length ?? 0) === 0) {
                return;
            }

            formikProps.setFieldValue(`customerBonusSettlements.${index}.isFinished`, true);
        });
    };

    const handleSaveFiles = (uploadedFiles: File[], deletedFileIds: number[]): void => {
        if (!bonusSettlementId || !supplierBonusSettlementId) {
            return;
        }

        setFilesCount(documents.length + (uploadedFiles.length - deletedFileIds.length));

        dispatch(
            updateSupplierBonusSettlementFiles({
                bonusSettlementId: +bonusSettlementId,
                supplierBonusSettlementId: +supplierBonusSettlementId,
                files: uploadedFiles,
                deletedFileIds,
            })
        );
    };

    const editOrAddTranslationId =
        formikProps.values.noteFromSupplier.length > 0
            ? 'app.bonusSettlements.supplierDetail.editComment'
            : 'app.bonusSettlements.supplierDetail.addComment';

    const readOnlyTranslationId = readOnly ? 'app.bonusSettlements.supplierDetail.showComment' : undefined;
    const commentTranslationId = readOnlyTranslationId ?? editOrAddTranslationId;

    const isNotFinished =
        formikProps.values.customerBonusSettlements.some(settlement => !settlement.isFinished) ||
        bonusSettlementStatus === BonusSettlementStatus.Draft;

    const canAddCustomer = [
        BonusSettlementSupplierStatus.Open,
        BonusSettlementSupplierStatus.Reclaimed,
        BonusSettlementSupplierStatus.PartiallySent,
    ].includes(bonusSettlementSupplierStatus);

    return (
        <>
            <Paper
                sx={{
                    py: 1,
                    px: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderTop: theme => `1px solid ${theme.palette.divider}`,
                }}
            >
                <Stack direction="row" gap={2}>
                    {canAddCustomer && (
                        <PoolNextButton
                            startIcon={<AddUserIcon />}
                            variant="text"
                            onClick={() => setIsAddUserModalOpen(true)}
                        >
                            <FormattedMessage id={'app.bonusSettlements.supplierDetail.addCustomer'} />
                        </PoolNextButton>
                    )}
                    <PoolNextButton
                        startIcon={<CommentNoteIcon />}
                        variant="text"
                        onClick={() => setIsCommentModalOpen(true)}
                    >
                        <FormattedMessage id={commentTranslationId} />
                    </PoolNextButton>
                    <PoolNextButton startIcon={<AttachIcon />} variant="text" onClick={() => setIsFilesModalOpen(true)}>
                        <FormattedMessage id="app.common.attachedFilesWithCount" values={{ count: filesCount }} />
                    </PoolNextButton>
                </Stack>

                {!readOnly && (
                    <Stack direction="row" gap={2}>
                        <PoolNextButton
                            startIcon={<CheckboxRoundActiveIcon fillColor={theme.palette.success.main} />}
                            variant="text"
                            onClick={() => handleAllFinishedClick(formikProps)}
                        >
                            <FormattedMessage id="app.bonusSettlements.supplierDetail.allFinishedCheck" />
                        </PoolNextButton>

                        <PoolTooltip
                            title={
                                isNotFinished ? (
                                    <FormattedMessage id="app.bonusSettlements.supplierDetail.submit.tooltip" />
                                ) : (
                                    ''
                                )
                            }
                            PopperProps={{
                                sx: { textAlign: 'center' },
                            }}
                        >
                            <PoolNextButton
                                onClick={() => formikProps.submitForm()}
                                disabled={isNotFinished || isSubmitting}
                            >
                                <FormattedMessage id="app.bonusSettlements.supplierDetail.submit" />
                            </PoolNextButton>
                        </PoolTooltip>
                    </Stack>
                )}
            </Paper>

            <BonusSettlementSupplierDetailCommentModal
                open={isCommentModalOpen}
                onClose={() => setIsCommentModalOpen(false)}
                translationId={commentTranslationId}
            />

            <PoolAttachmentsModal
                downloadBaseUrl={privateFilesystemPath}
                open={isFilesModalOpen}
                files={documents}
                onSave={handleSaveFiles}
                onClose={() => setIsFilesModalOpen(false)}
            />
            <BonusSettlementSupplierDetailAddCustomerModal
                open={isAddUserModalOpen}
                onClose={() => setIsAddUserModalOpen(false)}
            />
        </>
    );
};
